import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import {
  MagicLinkError,
  set_password as set_password_call,
  verify_magic_link,
} from '../api/magic_link_client';
import { get_welcome_translations, resolve_locale } from '../i18n/welcome_translations';
import { read_token, validate_password } from '../lib/welcome_logic';
import { AuthCard } from '../ui/auth_card';
import { TextField } from '../ui/text_field';

type Phase = 'verifying' | 'form' | 'submitting' | 'success' | 'invalid';

/**
 * /change-password?token=… — the password-reset email link target. This is the
 * public-facing landing page configured in the kernel person config form. It
 * verifies the `password_reset` magic-link token, shows the username, and lets
 * the member choose a new password. It reuses the same kernel endpoints as the
 * /welcome/set-password (initial-register) flow — only the token `purpose`
 * differs.
 */
export function ChangePasswordRoute(): ReactNode {
  // Captured once at mount: the effect strips the token from the URL via
  // replaceState, so re-reading window.location.search on later renders would
  // yield '' and recompute `token` to null — re-firing the effect and
  // clobbering the verified `form` phase with `invalid`.
  const [search] = useState(() => window.location.search);
  const t = useMemo(() => get_welcome_translations(resolve_locale(search)), [search]);
  const token = useMemo(() => read_token(search), [search]);
  const app_url = (import.meta.env['VITE_MEMBER_APP_URL'] as string | undefined) ?? '';

  const [phase, set_phase] = useState<Phase>('verifying');
  const [username, set_username] = useState('');
  const [password, set_password_value] = useState('');
  const [password_repeat, set_password_repeat] = useState('');
  const [error, set_error] = useState<string | null>(null);

  useEffect(() => {
    if (token === null) {
      set_phase('invalid');
      return;
    }
    // Strip the token from the URL bar (no history/referrer leak).
    window.history.replaceState({}, '', window.location.pathname);
    void verify_magic_link(token)
      .then((res) => {
        if (res.valid === true && res.purpose === 'password_reset') {
          set_username(res.username ?? '');
          set_phase('form');
        } else {
          set_phase('invalid');
        }
      })
      .catch(() => set_phase('invalid'));
  }, [token]);

  async function on_submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (token === null) return;
    const validation = validate_password(password, password_repeat);
    if (validation !== null) {
      set_error(t[validation]);
      return;
    }
    set_error(null);
    set_phase('submitting');
    try {
      await set_password_call({ token, password, password_repeat });
      set_phase('success');
    } catch (e) {
      const code = e instanceof Error ? e.message : '';
      // Token died between verify and submit → treat as invalid link.
      if (code === 'BAD_REQUEST') {
        set_phase('invalid');
        return;
      }
      // Backend password-policy rejection (kernel returns VALIDATION_ERROR / 422).
      // The kernel's rule text depends on its password_strength config, so show
      // its own message when it sent one rather than a generic stand-in.
      const server_messages = e instanceof MagicLinkError ? e.details : [];

      if (code === 'VALIDATION_ERROR' && server_messages.length > 0) {
        set_error(server_messages[0] ?? t.error_password_policy);
      } else if (code === 'VALIDATION_ERROR') {
        set_error(t.error_password_policy);
      } else {
        set_error(t.error_unexpected);
      }

      set_phase('form');
    }
  }

  if (phase === 'verifying') {
    return (
      <AuthCard title={t.reset_heading}>
        <div className="uf-loading">
          <span className="uf-loading__dots" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
          {t.verifying}
        </div>
      </AuthCard>
    );
  }

  if (phase === 'invalid') {
    return (
      <AuthCard title={t.link_invalid_heading}>
        <p className="uf-lead">{t.link_invalid_message}</p>
      </AuthCard>
    );
  }

  if (phase === 'success') {
    return (
      <AuthCard title={t.reset_success_heading}>
        <p className="uf-lead">{t.reset_success_message}</p>
        {username !== '' ? (
          <div className="uf-user">
            <span className="uf-user__label">{t.username_label}</span>
            <span className="uf-user__value">{username}</span>
          </div>
        ) : null}
        {app_url !== '' ? (
          <a className="uf-btn" href={app_url}>
            {t.open_in_app}
          </a>
        ) : null}
      </AuthCard>
    );
  }

  // phase === 'form' | 'submitting'
  const is_submitting = phase === 'submitting';

  return (
    <AuthCard title={t.reset_heading}>
      <p className="uf-lead">{t.reset_description}</p>
      {username !== '' ? (
        <div className="uf-user">
          <span className="uf-user__label">{t.username_label}</span>
          <span className="uf-user__value">{username}</span>
        </div>
      ) : null}
      <form className="uf-form" onSubmit={on_submit}>
        <p className="uf-hint">{t.password_hint}</p>
        <TextField
          label={t.password_label}
          value={password}
          on_change={set_password_value}
          type="password"
          autocomplete="new-password"
          disabled={is_submitting}
        />
        <TextField
          label={t.password_repeat_label}
          value={password_repeat}
          on_change={set_password_repeat}
          type="password"
          autocomplete="new-password"
          disabled={is_submitting}
          on_enter={() => {
            if (is_submitting === false) {
              void on_submit(new Event('submit') as unknown as FormEvent);
            }
          }}
        />
        {error !== null ? (
          <p className="uf-alert" role="alert">
            {error}
          </p>
        ) : null}
        <button type="submit" className="uf-btn" disabled={is_submitting}>
          {is_submitting === true ? t.submitting : t.set_submit}
        </button>
      </form>
    </AuthCard>
  );
}
